/**
 * MessagingArea — WhatsApp & SMS Chat-Ansicht
 *
 * Gemeinsame Chat-Bubble-Ansicht für WhatsApp und SMS Nachrichten.
 * Wiederverwendet das Chat-UI-Muster aus dem Team-Chat, angepasst für
 * externe Kommunikation mit Kunden.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import {
  ArrowLeft, Send, Paperclip, Search, MoreVertical,
  MessageCircle, Smartphone, Loader2, Clock,
  CheckCheck, Check, AlertCircle, Image as ImageIcon,
  Phone as PhoneIcon, User, Info, Smile,
} from 'lucide-react';
import { toast } from 'sonner';
import type { TwilioMessage, MessagingConversation, TwilioConfig } from '@/hooks/useMessaging';

// ─── Types ──────────────────────────────────────────────────────────────────

interface MessagingAreaProps {
  conversation: MessagingConversation | null;
  messages: TwilioMessage[];
  sending: boolean;
  config: TwilioConfig;
  currentUserId: string;
  onSendMessage: (channel: 'whatsapp' | 'sms', to: string, body: string) => Promise<any>;
  onBack: () => void;
}

// ─── Hilfsfunktionen ────────────────────────────────────────────────────────

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatDateSeparator(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Heute';
  if (days === 1) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatPhoneNumber(phone: string): string {
  if (!phone) return '';
  // Format German numbers nicely
  const clean = phone.replace(/\s+/g, '');
  if (clean.startsWith('+49') && clean.length >= 12) {
    const area = clean.slice(3, 6);
    const rest = clean.slice(6);
    return `+49 ${area} ${rest.replace(/(\d{4})/g, '$1 ').trim()}`;
  }
  return phone;
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'queued': return <Clock className="w-3 h-3 text-gray-400" />;
    case 'sent': return <Check className="w-3 h-3 text-gray-400" />;
    case 'delivered': return <CheckCheck className="w-3 h-3 text-gray-400" />;
    case 'read': return <CheckCheck className="w-3 h-3 text-blue-500" />;
    case 'failed':
    case 'undelivered': return <AlertCircle className="w-3 h-3 text-red-500" />;
    default: return null;
  }
}

function getContactInitials(name: string | null, phone: string): string {
  if (name) {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return (name[0] || '?').toUpperCase();
  }
  // Use last 2 digits of phone number
  const digits = phone.replace(/[^0-9]/g, '');
  return digits.slice(-2);
}

// ─── Leerer Zustand ─────────────────────────────────────────────────────────

export function MessagingEmptyState({ channel }: { channel: 'whatsapp' | 'sms' }) {
  const isWhatsApp = channel === 'whatsapp';
  const Icon = isWhatsApp ? MessageCircle : Smartphone;
  const color = isWhatsApp ? '#25D366' : '#F59E0B';
  const bgColor = isWhatsApp ? '#F0FDF4' : '#FFFBEB';

  return (
    <div className="flex-1 flex items-center justify-center bg-gray-50/30">
      <div className="text-center px-6 max-w-md">
        <div
          className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: bgColor }}
        >
          <Icon className="w-10 h-10" style={{ color }} />
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">
          {isWhatsApp ? 'WhatsApp Business' : 'SMS'}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {isWhatsApp
            ? 'Kommunizieren Sie direkt mit Ihren Kunden über WhatsApp. Senden und empfangen Sie Nachrichten, Bilder und Dokumente.'
            : 'Senden und empfangen Sie SMS-Nachrichten direkt aus dem System.'}
        </p>
        <p className="text-xs text-gray-400">
          Wählen Sie eine Unterhaltung aus der Liste oder starten Sie eine neue Nachricht.
        </p>
      </div>
    </div>
  );
}

// ─── Nicht konfiguriert Hinweis ─────────────────────────────────────────────

function TwilioNotConfiguredBanner() {
  return (
    <div className="mx-4 my-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
      <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
      <div className="text-xs text-amber-700">
        <p className="font-medium">Twilio noch nicht verbunden</p>
        <p className="mt-0.5">Nachrichten werden gespeichert und gesendet, sobald die Twilio API-Zugangsdaten in den Einstellungen hinterlegt sind.</p>
      </div>
    </div>
  );
}

// ─── Hauptkomponente ────────────────────────────────────────────────────────

export default function MessagingArea({
  conversation,
  messages,
  sending,
  config,
  currentUserId,
  onSendMessage,
  onBack,
}: MessagingAreaProps) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!conversation) {
    return <MessagingEmptyState channel="whatsapp" />;
  }

  const isWhatsApp = conversation.channel === 'whatsapp';
  const channelColor = isWhatsApp ? '#25D366' : '#F59E0B';
  const ChannelIcon = isWhatsApp ? MessageCircle : Smartphone;

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    try {
      const result = await onSendMessage(conversation.channel, conversation.phoneNumber, text);
      if (result?.warning) {
        toast.warning(result.warning);
      }
    } catch (err: any) {
      toast.error('Fehler beim Senden', { description: err.message });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Group messages by date
  const groupedMessages = useMemo(() => {
    const groups: { date: string; messages: TwilioMessage[] }[] = [];
    for (const msg of messages) {
      const dateKey = new Date(msg.created_at).toDateString();
      const last = groups[groups.length - 1];
      if (last && last.date === dateKey) {
        last.messages.push(msg);
      } else {
        groups.push({ date: dateKey, messages: [msg] });
      }
    }
    return groups;
  }, [messages]);

  const displayName = conversation.contactName || formatPhoneNumber(conversation.phoneNumber);
  const initials = getContactInitials(conversation.contactName, conversation.phoneNumber);

  return (
    <div className="flex-1 flex flex-col bg-white h-full min-w-0">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white min-h-[52px] flex-shrink-0">
        <button onClick={onBack} className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-4 h-4 text-gray-500" />
        </button>
        <div className="relative">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white"
            style={{ backgroundColor: channelColor }}
          >
            {initials}
          </div>
          <div
            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-white"
          >
            <ChannelIcon className="w-3 h-3" style={{ color: channelColor }} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-800 truncate">{displayName}</h3>
          <p className="text-[10px] text-gray-400 flex items-center gap-1">
            <ChannelIcon className="w-3 h-3" />
            {isWhatsApp ? 'WhatsApp' : 'SMS'} &middot; {formatPhoneNumber(conversation.phoneNumber)}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {isWhatsApp && (
            <button
              onClick={() => toast.info('Anrufen', { description: 'Anruf-Funktion wird bald verfügbar sein.' })}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
              aria-label="Anrufen"
              title="Anrufen"
            >
              <PhoneIcon className="w-4 h-4 text-gray-400" />
            </button>
          )}
          <button
            onClick={() => toast.info('Suche', { description: 'Nachrichtensuche wird bald verfügbar sein.' })}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
            aria-label="Suche"
            title="Suchen"
          >
            <Search className="w-4 h-4 text-gray-400" />
          </button>
          <button
            onClick={() => toast.info('Optionen', { description: 'Weitere Optionen werden bald verfügbar sein.' })}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
            aria-label="Optionen"
            title="Mehr Optionen"
          >
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Twilio not configured banner */}
      {!config.configured && <TwilioNotConfiguredBanner />}

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto px-4 py-3"
        style={{
          backgroundColor: isWhatsApp ? '#E5DDD5' : '#F3F4F6',
          backgroundImage: isWhatsApp
            ? 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d4cfc4\' fill-opacity=\'0.3\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
            : 'none',
        }}
      >
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <ChannelIcon className="w-12 h-12 mx-auto mb-3" style={{ color: `${channelColor}40` }} />
              <p className="text-sm text-gray-400">Noch keine Nachrichten</p>
              <p className="text-xs text-gray-400 mt-1">
                Senden Sie die erste Nachricht an {displayName}
              </p>
            </div>
          </div>
        ) : (
          <>
            {groupedMessages.map((group, gi) => (
              <div key={gi}>
                <div className="flex items-center justify-center my-3">
                  <span className="bg-white/90 text-[10px] text-gray-500 px-3 py-1 rounded-full shadow-sm">
                    {formatDateSeparator(group.date)}
                  </span>
                </div>
                {group.messages.map((msg) => {
                  const isOutbound = msg.direction === 'outbound';
                  const bubbleColor = isOutbound
                    ? (isWhatsApp ? 'bg-[#DCF8C6]' : 'bg-blue-100')
                    : 'bg-white';

                  return (
                    <div
                      key={msg.id}
                      className={`flex mb-1.5 ${isOutbound ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`relative max-w-[75%] sm:max-w-[65%] rounded-lg px-3 py-2 shadow-sm ${bubbleColor}`}>
                        {/* Media */}
                        {msg.media_urls && msg.media_urls.length > 0 && (
                          <div className="mb-1">
                            {msg.media_urls.map((url, i) => (
                              <img
                                key={i}
                                src={url}
                                alt="Medienanhang"
                                className="rounded-lg max-w-full h-auto max-h-48 object-cover"
                                loading="lazy"
                              />
                            ))}
                          </div>
                        )}

                        {/* Text */}
                        {msg.body && (
                          <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                            {msg.body}
                          </p>
                        )}

                        {/* Template indicator */}
                        {msg.wa_template_name && (
                          <p className="text-[10px] text-gray-400 italic mt-0.5">
                            Vorlage: {msg.wa_template_name}
                          </p>
                        )}

                        {/* Time + Status */}
                        <div className={`flex items-center gap-1 mt-0.5 ${isOutbound ? 'justify-end' : ''}`}>
                          <span className="text-[10px] text-gray-500">
                            {formatTime(msg.created_at)}
                          </span>
                          {isOutbound && getStatusIcon(msg.status)}
                        </div>

                        {/* Error message */}
                        {msg.error_message && (
                          <p className="text-[10px] text-red-500 mt-0.5 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {msg.error_message}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-gray-100 bg-white px-3 py-2 flex items-end gap-2 flex-shrink-0">
        <button
          onClick={() => toast.info('Emoji', { description: 'Emoji-Auswahl wird bald verfügbar sein.' })}
          className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 flex-shrink-0"
          aria-label="Emoji einfügen"
        >
          <Smile className="w-5 h-5" />
        </button>
        <button
          onClick={() => toast.info('Anhang', { description: 'Datei-Anhang wird bald verfügbar sein.' })}
          className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 flex-shrink-0"
          aria-label="Datei anhängen"
        >
          <Paperclip className="w-5 h-5" />
        </button>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Nachricht schreiben..."
          className="flex-1 resize-none bg-gray-100 rounded-2xl px-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-green-200 min-h-[40px] max-h-[120px]"
          rows={1}
          style={{
            height: 'auto',
            minHeight: '40px',
          }}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
          }}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || sending}
          className="p-2 rounded-full flex-shrink-0 transition-colors disabled:opacity-50"
          style={{
            backgroundColor: input.trim() ? channelColor : '#E5E7EB',
            color: input.trim() ? 'white' : '#9CA3AF',
          }}
          aria-label="Nachricht senden"
        >
          {sending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Neue Nachricht Dialog ──────────────────────────────────────────────────

interface NewMessageDialogProps {
  channel: 'whatsapp' | 'sms';
  onClose: () => void;
  onSend: (channel: 'whatsapp' | 'sms', to: string, body: string) => Promise<any>;
}

export function NewMessageDialog({ channel, onClose, onSend }: NewMessageDialogProps) {
  const [phone, setPhone] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const isWhatsApp = channel === 'whatsapp';
  const channelColor = isWhatsApp ? '#25D366' : '#F59E0B';
  const ChannelIcon = isWhatsApp ? MessageCircle : Smartphone;

  const handleSend = async () => {
    if (!phone.trim() || !body.trim()) return;

    // Normalize phone number
    let normalized = phone.trim().replace(/\s+/g, '');
    if (normalized.startsWith('0')) {
      normalized = '+49' + normalized.slice(1);
    }
    if (!normalized.startsWith('+')) {
      normalized = '+49' + normalized;
    }

    setSending(true);
    try {
      const result = await onSend(channel, normalized, body.trim());
      if (result?.warning) {
        toast.warning(result.warning);
      }
      toast.success('Nachricht gesendet');
      onClose();
    } catch (err: any) {
      toast.error('Fehler', { description: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${channelColor}20` }}
          >
            <ChannelIcon className="w-5 h-5" style={{ color: channelColor }} />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-gray-800">
              Neue {isWhatsApp ? 'WhatsApp' : 'SMS'}-Nachricht
            </h3>
            <p className="text-xs text-gray-500">
              {isWhatsApp ? 'Senden Sie eine WhatsApp-Nachricht' : 'Senden Sie eine SMS'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Telefonnummer
            </label>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-600">
                <span>🇩🇪</span>
                <span>+49</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="176 12345678"
                className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Nachricht
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={isWhatsApp ? 'WhatsApp-Nachricht schreiben...' : 'SMS-Text schreiben...'}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 min-h-[100px] resize-none"
              rows={4}
            />
            {!isWhatsApp && (
              <p className="text-[10px] text-gray-400 mt-1">
                {body.length}/160 Zeichen {body.length > 160 ? `(${Math.ceil(body.length / 153)} SMS)` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSend}
            disabled={!phone.trim() || !body.trim() || sending}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: channelColor }}
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Senden
          </button>
        </div>
      </div>
    </div>
  );
}
